import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { SectionTitle } from 'components/Section';
import Subtitle from 'components/SubTitle';
import { Row, List, Col, Tag, Input, Divider, Menu, Button } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import Link from 'next/link';
import Content from 'layout/Basic/Content';
import Sider from 'layout/Basic/Sider';
import { Rectangle, SkyscraperMobile } from 'components/Ads';
import EventCard from 'components/EventCard';
import Section from 'components/Section';
import CompanyCarousel from 'components/CompanyCarousel';
import ArticleItem from 'components/List/ArticleItem';
import CompanyItem from './Item';
import CardGrid from 'components/CardGrid';
import MultimediaCardGrid from 'components/MultimediaCardGrid';

const { CheckableTag } = Tag;
const { SubMenu } = Menu;

const Search = styled(Input)`
    border-width: 0 0 1px 0;
`;

const SubMenuStyled = styled(SubMenu)`
    a {
        color: ${({ theme }) => theme.colors.greyishBrownTwo};
    }
`;

const CheckableTagStyled = styled(CheckableTag)`
    @media (max-width: 768px) {
        font-size: 24px;
        padding: 10px;
        width: 74px;
        height: 42px;
    }
    text-align: center;
    font-size: 14px;
    padding: 8px;

    font-weight: 300;
    border: 1px solid #b7cbd9;
    margin: 0;
`;

export interface Props {
    ads: PageAds;
    articles: Article[];
    branches: Branch[];
    events: Occurrence[];
    companies: Company[];
    videos: Video[];
    galleries: Gallery[];
    premiumCompanies: Company[];
    whitePapers: WhitePaper[];
}

const CompanyIndex = ({
    articles,
    branches,
    events,
    companies = [],
    galleries,
    videos,
    premiumCompanies,
    whitePapers,
    ads
}: Props): React.JSX.Element => {
    const [char, setChar] = useState(null);
    const [name, setName] = useState('');
    const router = useRouter();
    useEffect(() => {
        const { query } = router;
        if ('letter' in query) {
            setChar(query.letter);
        }

        if ('name' in query) {
            setName(query.name as string);
        }
    }, []);

    const searchByChar: string[] = [
        'A',
        'B',
        'C',
        'D',
        'E',
        'F',
        'G',
        'H',
        'I',
        'J',
        'K',
        'L',
        'M',
        'N',
        'O',
        'P',
        'Q',
        'R',
        'S',
        'T',
        'U',
        'V',
        'W',
        'X',
        'Y',
        'Z',
        '0-9'
    ];

    const handleSearchByChar = (char: string, checked: boolean) => {
        if (checked && name) {
            setName(null);
        }
        if (checked) {
            setChar(char);
            router.push(`?letter=${char}`);
        } else {
            setChar(null);
            router.push(router.pathname);
        }
    };

    const onNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (char) {
            setChar(null);
        }
        setName(e.target.value);
    };

    const onSearch = () => {
        if (name) {
            router.push(`?name=${name}`);
        } else {
            router.push(router.pathname);
        }
    };

    const onBranchClick = () => {
        if (name) {
            setName(null);
        }
        if (char) {
            setChar(null);
        }
    };

    const companyList = useMemo(() => {
        const sorter = (a: Company, b: Company) => {
            const nameA = a.name.toUpperCase();
            const nameB = b.name.toUpperCase();
            if (nameA < nameB) {
                return -1;
            }
            if (nameA > nameB) {
                return 1;
            }
            return 0;
        };
        const premiumCompanies = companies.filter((company) => company.premium).sort(sorter);
        const standardCompanies = companies.filter((company) => !company.premium).sort(sorter);
        return [...premiumCompanies, ...standardCompanies];
    }, [companies]);
    const { rectangle, skyscraper } = ads;
    return (
        <>
            <Section gutter={16}>
                <Content>
                    <Row>
                        <Col span={24}>
                            <SectionTitle title="Firmy"></SectionTitle>
                        </Col>

                        <Col>
                            <Subtitle category="Szukaj po kategoriach" />
                        </Col>
                        <Col span={24} style={{ paddingTop: 15 }}>
                            <Menu
                                onClick={onBranchClick}
                                mode="horizontal"
                                style={{
                                    position: 'relative',
                                    display: 'flex',
                                    justifyContent: 'center'
                                }}
                            >
                                {branches?.map((branch) => {
                                    if (branch.children.length === 0) {
                                        return (
                                            <Menu.Item key={branch.seoSlug}>
                                                <Link href={`?branch=${branch.seoSlug}`}>
                                                    {branch.title}
                                                </Link>
                                            </Menu.Item>
                                        );
                                    }
                                    return (
                                        <SubMenuStyled
                                            key={branch.seoSlug}
                                            title={
                                                <Link href={`?branch=${branch.seoSlug}`}>
                                                    {branch.title}
                                                </Link>
                                            }
                                        >
                                            {branch.children.map((child) => (
                                                <Menu.Item key={child.seoSlug}>
                                                    <Link href={`?branch=${child.seoSlug}`}>
                                                        {child.title}
                                                    </Link>
                                                </Menu.Item>
                                            ))}
                                        </SubMenuStyled>
                                    );
                                })}
                            </Menu>
                        </Col>
                        <Col span={24} style={{ paddingTop: 60 }}>
                            <Row>
                                <Col>
                                    <Subtitle category="Szukaj po nazwie" />
                                </Col>
                            </Row>
                            <Section justify="center" gutter={[16, 16]}>
                                {searchByChar.map((tag) => (
                                    <Col xs={6} sm={4} md={2} lg={1} key={tag}>
                                        <CheckableTagStyled
                                            key={tag}
                                            checked={char === tag}
                                            onChange={(checked) => handleSearchByChar(tag, checked)}
                                        >
                                            {tag}
                                        </CheckableTagStyled>
                                    </Col>
                                ))}
                            </Section>
                            <Section>
                                <Col xs={24} md={12}>
                                    <Search
                                        placeholder="Wpisz nazwę firmy"
                                        size="large"
                                        allowClear
                                        value={name}
                                        onChange={onNameChange}
                                        onPressEnter={onSearch}
                                        suffix={
                                            <Button type="link" onClick={onSearch}>
                                                <SearchOutlined
                                                    style={{
                                                        fontSize: 16,
                                                        color: '#1890ff'
                                                    }}
                                                />
                                            </Button>
                                        }
                                    />
                                </Col>
                            </Section>
                            <Row justify="center">
                                <Col xs={24} sm={12}>
                                    <SkyscraperMobile
                                        id={skyscraper[0]}
                                        margin="24px auto 0 auto"
                                    />
                                </Col>
                                <Col xs={24} sm={12}>
                                    <SkyscraperMobile
                                        id={skyscraper[1]}
                                        margin="24px auto 0 auto"
                                    />
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                    {companyList && (
                        <Section $marginTop={40}>
                            <Col span={24}>
                                <List
                                    itemLayout="horizontal"
                                    dataSource={companyList}
                                    renderItem={(item: Company) => <CompanyItem item={item} />}
                                />
                            </Col>
                        </Section>
                    )}
                    <Section $marginTop={60} gutter={[16, 24]}>
                        <Col span={24}>
                            <SectionTitle title="Wyróżnione firmy" />
                        </Col>
                        <Col span={24} style={{ marginTop: 20 }}>
                            <CompanyCarousel companies={premiumCompanies} />
                        </Col>
                    </Section>
                    {articles && (
                        <Row>
                            <Col span={24}>
                                <SectionTitle title="Ostatnio dodane artykuły" />
                            </Col>
                            <Col span={24}>
                                <Row gutter={[16, 16]}>
                                    {articles.map((item: Article) => {
                                        const companyAsCategory = (item.companies || []).find(
                                            (company) => company.sponsor === true
                                        );
                                        return (
                                            <Col span={24} key={item.seoSlug}>
                                                <ArticleItem
                                                    $article={item}
                                                    companyAsCategory={companyAsCategory}
                                                />
                                            </Col>
                                        );
                                    })}
                                </Row>
                            </Col>
                        </Row>
                    )}
                    <Divider />
                    {whitePapers && (
                        <Section gutter={24}>
                            <Col span={24}>
                                <CardGrid
                                    type="white-paper"
                                    items={whitePapers}
                                    url="/white-papers"
                                    title="White Papers"
                                />
                            </Col>
                        </Section>
                    )}
                </Content>
                <Sider>
                    <Row>
                        <Col span={24}>
                            <Rectangle id={rectangle[0]} margin="16px auto" />
                        </Col>
                        <Col span={24}>
                            <Rectangle id={rectangle[1]} margin="16px auto" />
                        </Col>
                        <Col span={24}>
                            <Rectangle id={rectangle[2]} margin="16px auto" />
                        </Col>
                    </Row>
                    <Section>
                        <Col span={24}>
                            <SectionTitle title="Wydarzenia" url="/wydarzenia" />
                            <Subtitle category="Najbliższe polecane wydarzenia" />
                            {events?.map((event) => {
                                const last = events.indexOf(event) === events.length - 1;
                                return (
                                    <div key={`event-${event.seoSlug}`}>
                                        <EventCard
                                            key={`event-${event.seoSlug}`}
                                            title={event.name}
                                            type={event.type}
                                            slug={event.seoSlug}
                                            startDate={event.startDate}
                                            endDate={event.endDate}
                                        />
                                        {!last && <Divider />}
                                    </div>
                                );
                            })}
                        </Col>
                    </Section>
                    <Row>
                        <Col span={24}>
                            <Rectangle id={rectangle[3]} margin="16px auto" />
                        </Col>
                        <Col span={24}>
                            <Rectangle id={rectangle[4]} margin="16px auto" />
                        </Col>
                    </Row>
                </Sider>
            </Section>
            <Divider />
            <Section>
                <MultimediaCardGrid galleries={galleries} videos={videos} title="Multimedia" />
            </Section>
        </>
    );
};

export default CompanyIndex;
